import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { cn } from '@/lib/utils'
import { ReactNode, useCallback } from 'react'
import { Finder } from './Finder'
import { FinderCreateSlot, FinderItem } from './types'

interface FinderDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    trigger?: ReactNode
    initialFolderId?: string | null

    // Selection mode: 'browse' just navigates, 'select' allows picking files
    mode?: 'browse' | 'select'

    // Simple selection callback - for most use cases
    onSelect?: (files: FinderItem[]) => void
    selectText?: string
    isSelectLoading?: boolean

    // Custom footer actions - for complex cases (e.g., Overwrite/Append buttons)
    renderActions?: (context: {
        selectedFile: FinderItem | null
        selectedFiles: FinderItem[]
        onConfirm: () => void
        onCancel: () => void
    }) => ReactNode

    // Filtering and selection control
    showOnly?: 'all' | 'folders' | 'files'
    selectionMode?: 'single' | 'multiple'

    // Optional create actions (e.g. New Document in picker toolbar)
    createFolderButton?: FinderCreateSlot
    createFileButton?: FinderCreateSlot
    createFolderDropdownItem?: FinderCreateSlot
    createFileDropdownItem?: FinderCreateSlot
}

export function FinderDialog({
    open,
    onOpenChange,
    trigger,
    initialFolderId,
    mode = 'browse',
    onSelect,
    selectText = 'Select',
    isSelectLoading = false,
    renderActions,
    showOnly = 'all',
    selectionMode = 'multiple',
    createFolderButton,
    createFileButton,
    createFolderDropdownItem,
    createFileDropdownItem,
}: FinderDialogProps) {
    const close = useCallback(() => {
        onOpenChange(false)
    }, [onOpenChange])

    const handleConfirmSelect = useCallback(
        (files: FinderItem[]) => {
            onSelect?.(files)
            close()
        },
        [onSelect, close]
    )

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
            <DialogContent
                className={cn(
                    'left-[50%] top-[50%] w-[calc(100vw-24px)] max-w-6xl h-[min(85dvh,720px)] translate-x-[-50%] translate-y-[-50%]',
                    'p-0 overflow-hidden border-0 bg-transparent shadow-none',
                    // Hide default dialog close — Finder renders its own aligned to the panel
                    '[&>button]:hidden'
                )}
            >
                <DialogHeader>
                    <VisuallyHidden>
                        <DialogTitle>Choose documents</DialogTitle>
                    </VisuallyHidden>
                    <VisuallyHidden>
                        <DialogDescription>Select one or more documents, then confirm.</DialogDescription>
                    </VisuallyHidden>
                </DialogHeader>
                <div className='h-full w-full'>
                    <Finder
                        className='h-full'
                        onClose={close}
                        initialFolderId={initialFolderId}
                        selectMode={mode === 'select'}
                        onConfirmSelect={handleConfirmSelect}
                        selectText={selectText}
                        isSelectLoading={isSelectLoading}
                        showOnly={showOnly}
                        selectionMode={selectionMode}
                        createFolderButton={createFolderButton}
                        createFileButton={createFileButton}
                        createFolderDropdownItem={createFolderDropdownItem}
                        createFileDropdownItem={createFileDropdownItem}
                        renderSelectActions={
                            renderActions
                                ? ({ selectedFile, selectedFiles, onConfirm, onCancel }) => (
                                      <>
                                          {renderActions({
                                              selectedFile,
                                              selectedFiles,
                                              onConfirm,
                                              onCancel,
                                          })}
                                      </>
                                  )
                                : undefined
                        }
                    />
                </div>
            </DialogContent>
        </Dialog>
    )
}
